import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { api } from '../lib/api'
import { ArrowRight, Check, X, AlertCircle, Trash2, Plus } from 'lucide-react'
import toast from 'react-hot-toast'

interface TransitionWizardProps {
  unitType: string
  unitId: number
  unitName?: string
  onClose: () => void
  onComplete?: () => void
}

function getSuccessorDisplayName(s: any): string {
  return s.class_name || s.course_name || s.group_name || `#${s.successor_id}`
}

export default function TransitionWizard({
  unitType,
  unitId,
  unitName,
  onClose,
  onComplete
}: TransitionWizardProps) {
  const [step, setStep] = useState<'successor' | 'suggestions' | 'review' | 'result'>('successor')
  const [selectedTransitions, setSelectedTransitions] = useState<any[]>([])
  const [transitionDate, setTransitionDate] = useState(
    new Date().toISOString().split('T')[0]
  )
  const [newSuccessorId, setNewSuccessorId] = useState<string>('')

  const queryClient = useQueryClient()

  const typeLabel = unitType === 'class' ? 'Klasse' : unitType === 'course' ? 'Kurs' : 'Gruppe'
  const displayName = unitName || `${typeLabel} #${unitId}`

  // Aktuelle Nachfolger
  const { data: successorsData, isLoading: successorsLoading } = useQuery(
    ['unit-successors', unitType, unitId],
    () => api.get(`/units/${unitType}/${unitId}/successors`).then(res => res.data),
    { staleTime: 30_000 }
  )
  const successors = successorsData?.data ?? []

  // Alle Einheiten (für Dropdown), aktive, ohne aktuelle
  const { data: unitsData } = useQuery(
    ['units-list', unitType],
    () => api.get(`/units/${unitType}`, { params: { is_active: 'true' } }).then(res => res.data),
    { staleTime: 60_000 }
  )
  const allUnits = (unitsData?.data ?? []).filter((u: any) => u.id !== unitId)
  const unitNameKey = unitType === 'class' ? 'class_name' : 'name'

  const addSuccessorMutation = useMutation(
    (successorId: number) =>
      api.post(`/units/${unitType}/${unitId}/successors`, {
        successor_type: unitType,
        successor_id: successorId
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['unit-successors', unitType, unitId])
        queryClient.invalidateQueries(['transition-suggestions', unitType, unitId])
        setNewSuccessorId('')
        toast.success('Nachfolger hinzugefügt')
      },
      onError: (err: any) => {
        toast.error(err?.response?.data?.error || 'Nachfolger konnte nicht hinzugefügt werden')
      }
    }
  )

  const removeSuccessorMutation = useMutation(
    (successorRowId: number) =>
      api.delete(`/units/${unitType}/${unitId}/successors/${successorRowId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['unit-successors', unitType, unitId])
        queryClient.invalidateQueries(['transition-suggestions', unitType, unitId])
        toast.success('Nachfolger entfernt')
      },
      onError: () => { toast.error('Nachfolger konnte nicht entfernt werden') }
    }
  )

  const handleAddSuccessor = () => {
    const id = parseInt(newSuccessorId, 10)
    if (!id) return
    addSuccessorMutation.mutate(id)
  }

  // Übergangsvorschläge (nur wenn mindestens ein Nachfolger)
  const { data: suggestionsData, isLoading: suggestionsLoading } = useQuery(
    ['transition-suggestions', unitType, unitId],
    () =>
      api
        .get('/transitions/suggestions', {
          params: { unit_type: unitType, unit_id: unitId }
        })
        .then(res => res.data),
    { enabled: step === 'suggestions' || step === 'review', staleTime: 30_000 }
  )
  const suggestions = suggestionsData?.data || []

  const executeMutation = useMutation(
    (transitions: any[]) =>
      api.post('/transitions/execute', {
        transitions,
        transition_date: transitionDate
      }),
    {
      onSuccess: () => {
        setStep('result')
        queryClient.invalidateQueries(['units', unitType])
        queryClient.invalidateQueries('memberships')
        toast.success('Übergänge erfolgreich durchgeführt')
        if (onComplete) setTimeout(onComplete, 2000)
      },
      onError: (error: any) => {
        console.error('Error executing transitions:', error)
        toast.error('Fehler beim Ausführen der Übergänge')
      }
    }
  )

  const handleSelectTransition = (transition: any) => {
    setSelectedTransitions(prev => {
      const exists = prev.find(t => t.current_membership_id === transition.current_membership_id)
      if (exists) return prev.filter(t => t.current_membership_id !== transition.current_membership_id)
      return [...prev, transition]
    })
  }

  const handleExecute = () => {
    if (selectedTransitions.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Übergang aus')
      return
    }
    if (!confirm(`Möchten Sie ${selectedTransitions.length} Übergänge wirklich durchführen?`)) return
    executeMutation.mutate(selectedTransitions)
  }

  const isLoading = successorsLoading || (step === 'suggestions' && suggestionsLoading)

  if (isLoading && step === 'successor') {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          Nachfolger &amp; Übergänge – {displayName}
        </h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Schritt 1: Nachfolger festlegen */}
      {step === 'successor' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Lernende wechseln von <strong>{displayName}</strong> in die nächste Einheit. Legen Sie fest, welche Einheit der Nachfolger ist (z.&nbsp;B. Klasse 07a → 08a). Danach können Sie die Übergänge durchführen.
          </p>

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Aktuelle Nachfolger</h4>
            {successors.length === 0 ? (
              <p className="text-sm text-gray-500">Noch kein Nachfolger festgelegt.</p>
            ) : (
              <ul className="space-y-2">
                {successors.map((s: any) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    <span>
                      {displayName} <ArrowRight className="inline h-4 w-4 mx-1 text-gray-400" /> {getSuccessorDisplayName(s)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeSuccessorMutation.mutate(s.id)}
                      disabled={removeSuccessorMutation.isLoading}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Nachfolger entfernen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              <select
                value={newSuccessorId}
                onChange={e => setNewSuccessorId(e.target.value)}
                className="rounded-md border border-gray-300 py-2 pl-3 pr-8 text-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">— Nachfolger wählen —</option>
                {allUnits.map((u: any) => (
                  <option key={u.id} value={u.id}>{u[unitNameKey] || u.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAddSuccessor}
                disabled={!newSuccessorId || addSuccessorMutation.isLoading}
                className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" /> Hinzufügen
              </button>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Schließen
            </button>
            <button
              onClick={() => setStep('suggestions')}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500"
            >
              Weiter: Übergänge durchführen
            </button>
          </div>
        </div>
      )}

      {/* Schritt 2: Übergänge durchführen */}
      {step === 'suggestions' && (
        <div className="space-y-4">
          {successors.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                Bitte legen Sie zuerst mindestens einen Nachfolger fest. Ohne Nachfolger können keine Übergänge erzeugt werden.
              </p>
              <button
                type="button"
                onClick={() => setStep('successor')}
                className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                ← Nachfolger festlegen
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                Wählen Sie das Übergangsdatum und die Lernenden, die in die Nachfolger-Einheit wechseln sollen.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Übergangsdatum</label>
                <input
                  type="date"
                  value={transitionDate}
                  onChange={e => setTransitionDate(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              {suggestionsLoading ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                </div>
              ) : suggestions.length === 0 ? (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
                  <AlertCircle className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                  <p>Keine Übergangsvorschläge – aktuell keine aktiven Mitglieder oder alle bereits zugeordnet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {suggestions.map((suggestion: any, index: number) => {
                    const isSelected = selectedTransitions.some(
                      t => t.current_membership_id === suggestion.current_membership_id
                    )
                    return (
                      <div
                        key={index}
                        className={`cursor-pointer rounded-lg border p-4 transition-colors ${
                          isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleSelectTransition(suggestion)}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectTransition(suggestion)}
                            className="rounded"
                          />
                          <span className="font-medium">
                            {suggestion.student_name ?? `Schüler #${suggestion.student_id}`}
                          </span>
                          <span className="text-gray-500 text-sm">
                            → {suggestion.to_unit_name ?? `${suggestion.to_unit_type} ${suggestion.to_unit_id}`}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setStep('successor')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Zurück
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Abbrechen
                </button>
                <button
                  onClick={() => setStep('review')}
                  disabled={selectedTransitions.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Weiter ({selectedTransitions.length} ausgewählt)
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Übersicht – Übergänge durchführen</h4>
          <div className="space-y-2">
            {selectedTransitions.map((transition: any, index: number) => (
              <div key={index} className="rounded-lg border border-gray-200 p-4 text-sm">
                <span>{transition.student_name ?? `Schüler #${transition.student_id}`}</span>
                <span className="text-gray-500"> → {transition.to_unit_name ?? `${transition.to_unit_type} ${transition.to_unit_id}`}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => setStep('suggestions')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Zurück
            </button>
            <button
              onClick={handleExecute}
              disabled={executeMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500 disabled:opacity-50"
            >
              {executeMutation.isLoading ? 'Wird ausgeführt...' : 'Übergänge durchführen'}
            </button>
          </div>
        </div>
      )}

      {step === 'result' && executeMutation.data && (
        <div className="space-y-4">
          <div className="rounded-lg border border-green-200 bg-green-50 p-4">
            <div className="flex items-center gap-2 text-green-800 mb-2">
              <Check className="h-5 w-5" />
              <span className="font-medium">Übergänge erfolgreich durchgeführt</span>
            </div>
            <div className="text-sm text-green-700 space-y-1">
              <p>Erstellt: {executeMutation.data.data.created}</p>
              <p>Beendet: {executeMutation.data.data.ended}</p>
              {executeMutation.data.data.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="font-medium">Fehler:</p>
                  <ul className="list-disc list-inside">
                    {executeMutation.data.data.errors.map((error: any, i: number) => (
                      <li key={i}>{error.error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500"
            >
              Schließen
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
